import React, { useMemo, useRef, useState } from 'react';
import { useAuthRedirect } from '../utils/authRedirect';

function formatMemberSince(value) {
    if (!value) {
        return 'Unknown';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return 'Unknown';
    }

    return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
}

function AccountPanel({ user, hasNoAdsState, recipeCount, tagCount, onLogout }) {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [isSuccess, setIsSuccess] = useState(false);
    const [deletePassword, setDeletePassword] = useState('');
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteMessage, setDeleteMessage] = useState('');

    const currentPasswordRef = useRef(null);
    const deletePasswordRef = useRef(null);

    const handleAuthError = useAuthRedirect();

    const memberSince = useMemo(() => formatMemberSince(user?.createdAt), [user?.createdAt]);
    const passwordMismatch = confirmNewPassword.length > 0 && newPassword !== confirmNewPassword;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage('');
        setIsSuccess(false);

        if (passwordMismatch) {
            setMessage('New passwords must match.');
            setLoading(false);
            return;
        }

        setLoading(true);

        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/auth/password', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'x-auth-token': token,
                },
                body: JSON.stringify({
                    currentPassword,
                    newPassword,
                    confirmNewPassword,
                }),
            });

            if (handleAuthError(response.status)) {
                return;
            }

            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                throw new Error(data.error || (data.errors && data.errors[0] && data.errors[0].msg) || 'Password update failed.');
            }

            setMessage(data.msg || 'Password updated successfully.');
            setIsSuccess(true);
            setCurrentPassword('');
            setNewPassword('');
            setConfirmNewPassword('');
        } catch (err) {
            setMessage(err.message || 'Password update failed.');
            setIsSuccess(false);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteAccount = async (e) => {
        e.preventDefault();
        setDeleteMessage('');

        if (!deletePassword.trim()) {
            setDeleteMessage('Current password is required.');
            return;
        }

        setDeleteLoading(true);
        let deleted = false;

        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/auth/account', {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'x-auth-token': token,
                },
                body: JSON.stringify({ currentPassword: deletePassword }),
            });

            if (handleAuthError(response.status)) {
                return;
            }

            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                throw new Error(data.error || 'Account deletion failed.');
            }

            setDeletePassword('');
            setDeleteMessage(data.msg || 'Account deleted successfully.');
            deleted = true;
        } catch (err) {
            setDeleteMessage(err.message || 'Account deletion failed.');
        } finally {
            setDeleteLoading(false);
        }

        if (deleted && onLogout) {
            onLogout();
        }
    };

    return (
        <div className="panel account-panel">
            <div className="d-flex justify-content-between align-items-start gap-3">
                <div>
                    <h5>Account</h5>
                    <p className="text-muted mb-0">Manage your login, profile summary, and account safety.</p>
                </div>
                <span className={`badge ${hasNoAdsState ? 'bg-success' : 'bg-warning text-dark'}`}>
                    {hasNoAdsState ? 'Ad-free' : 'Ads active'}
                </span>
            </div>

            <div className="account-stats mt-4">
                <div className="account-stat">
                    <span className="account-stat__label">Email</span>
                    <span className="account-stat__value">{user?.email || 'Unknown'}</span>
                </div>
                <div className="account-stat">
                    <span className="account-stat__label">Member since</span>
                    <span className="account-stat__value">{memberSince}</span>
                </div>
                {typeof recipeCount === 'number' && (
                    <div className="account-stat">
                        <span className="account-stat__label">Saved recipes</span>
                        <span className="account-stat__value">{recipeCount}</span>
                    </div>
                )}
                {typeof tagCount === 'number' && (
                    <div className="account-stat">
                        <span className="account-stat__label">Tags</span>
                        <span className="account-stat__value">{tagCount}</span>
                    </div>
                )}
            </div>

            <hr />

            <div>
                <h5>Change password</h5>
                <form onSubmit={handleSubmit} className="account-password-form mt-3">
                    <div className="mb-3">
                        <label htmlFor="currentPasswordInput" className="form-label">Current password</label>
                        <input
                            ref={currentPasswordRef}
                            type="password"
                            className="form-control"
                            id="currentPasswordInput"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            required
                            autoComplete="current-password"
                            aria-label="Current password"
                        />
                    </div>
                    <div className="mb-3">
                        <label htmlFor="newPasswordInput" className="form-label">New password</label>
                        <input
                            type="password"
                            className="form-control"
                            id="newPasswordInput"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            required
                            minLength={6}
                            autoComplete="new-password"
                            aria-label="New password"
                        />
                    </div>
                    <div className="mb-3">
                        <label htmlFor="confirmNewPasswordInput" className="form-label">Confirm new password</label>
                        <input
                            type="password"
                            className={`form-control ${passwordMismatch ? 'is-invalid' : ''}`}
                            id="confirmNewPasswordInput"
                            value={confirmNewPassword}
                            onChange={(e) => setConfirmNewPassword(e.target.value)}
                            required
                            minLength={6}
                            autoComplete="new-password"
                            aria-label="Confirm new password"
                        />
                        {passwordMismatch && (
                            <div className="invalid-feedback d-block">
                                New passwords must match.
                            </div>
                        )}
                    </div>
                    <button type="submit" className="btn btn-primary w-100" disabled={loading || passwordMismatch}>
                        {loading ? 'Updating password...' : 'Update password'}
                    </button>
                </form>
                {message && (
                    <div className={`alert mt-3 mb-0 ${isSuccess ? 'alert-success' : 'alert-danger'}`} role="status">
                        {message}
                    </div>
                )}
            </div>

            <hr />

            <div className="account-actions">
                <button type="button" className="btn btn-outline-secondary w-100" onClick={onLogout} aria-label="Sign out">
                    Sign out
                </button>
            </div>

            <hr />

            <div className="account-danger">
                <h5>Danger zone</h5>
                <p className="text-muted small mb-3">Delete your account and all saved recipes permanently.</p>
                <form onSubmit={handleDeleteAccount} className="account-delete-form">
                    <div className="mb-3">
                        <label htmlFor="deletePasswordInput" className="form-label">Current password</label>
                        <input
                            ref={deletePasswordRef}
                            type="password"
                            className="form-control"
                            id="deletePasswordInput"
                            value={deletePassword}
                            onChange={(e) => setDeletePassword(e.target.value)}
                            required
                            autoComplete="current-password"
                            aria-label="Current password for account deletion"
                        />
                    </div>
                    <button type="submit" className="btn btn-danger w-100" disabled={deleteLoading} aria-describedby="deleteMessage">
                        {deleteLoading ? 'Deleting account...' : 'Delete account'}
                    </button>
                    <div id="deleteMessage" className="sr-only">{deleteMessage}</div>
                </form>
                {deleteMessage && (
                    <div className={`alert mt-3 mb-0 ${deleteMessage.toLowerCase().includes('success') ? 'alert-success' : 'alert-danger'}`} role="status">
                        {deleteMessage}
                    </div>
                )}
            </div>
        </div>
    );
}

export default AccountPanel;
